import type { ShareEmailBetSlipDTO } from '@/types/share.types';

const FONT_FAMILY = 'Helvetica, Arial, sans-serif';
const BLUE = '#2563eb';
const HEADER_BG = '#F3F6F9';
const CARD_BG = '#ffffff';
const CHIP_BG = '#f3f4f6';
const BORDER_COLOR = '#e5e7eb';
const TEXT_COLOR = '#111827';

const BASE_URL = 'https://www.interactivebrokers.com/predictionmarkets/app';
const ELECTION_BOARD_PATH = '/#/election-hub/';

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function buildChipCell(label: string, padding: string): string {
  return `<td width="50%" style="padding: ${padding};">
  <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse; background-color: ${CHIP_BG}; border: 1px solid ${BORDER_COLOR};" bgcolor="${CHIP_BG}">
    <tr>
      <td align="center" style="padding: 10px 14px; font-family: ${FONT_FAMILY}; font-size: 14px; line-height: 18px; color: ${TEXT_COLOR}; white-space: nowrap;">
        <span style="color: ${TEXT_COLOR};">${escapeHtml(label)}</span>
      </td>
    </tr>
  </table>
</td>`;
}

function buildCandidateCard(title: string, titleColor: string, chips: { label: string }[]): string {
  const chipCells = chips
    .map((chip, i) => {
      const isFirst = i === 0;
      const padding = isFirst ? '0 4px 0 0' : '0 0 0 4px';
      return buildChipCell(chip.label, padding);
    })
    .join('\n');

  return `<tr>
  <td bgcolor="${HEADER_BG}" style="background-color: ${HEADER_BG}; padding: 6px 28px 12px 28px;">
    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse; background-color: ${CARD_BG}; border: 1px solid ${BORDER_COLOR};" bgcolor="${CARD_BG}">
      <tr>
        <td style="padding: 16px 20px;">
          <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse;">
            <tr>
              <td valign="middle" style="font-family: ${FONT_FAMILY}; font-size: 16px; line-height: 20px; color: ${escapeHtml(titleColor || BLUE)};">
                ${escapeHtml(title)}
              </td>
              <td width="50%" valign="middle" style="padding: 0 0 0 12px;">
                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse;">
                  <tr>
                    ${chipCells}
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </td>
</tr>`;
}

function buildCtaButton(label: string, href: string, isLast: boolean): string {
  const paddingRight = isLast ? '0' : '0 8px 0 0';
  return `<td style="padding: ${paddingRight};">
  <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse; background-color: #ACFAEA;" bgcolor="#ACFAEA">
    <tr>
      <td align="center" style="padding: 12px 20px; font-family: ${FONT_FAMILY}; font-size: 13px; line-height: 20px; color: ${TEXT_COLOR}; white-space: nowrap;">
        <a href="${escapeHtml(href)}" style="color: ${TEXT_COLOR}; text-decoration: none; font-family: ${FONT_FAMILY}; font-size: 13px;"><span style="color: ${TEXT_COLOR};">${escapeHtml(label)}</span></a>
      </td>
    </tr>
  </table>
</td>`;
}

export function buildShareEmailHtml(dto: ShareEmailBetSlipDTO): string {
  const headerRow = `<tr>
  <td bgcolor="${HEADER_BG}" style="background-color: ${HEADER_BG}; padding: 24px 28px 8px 28px;">
    <span style="font-family: ${FONT_FAMILY}; font-size: 16px; line-height: 24px; color: ${TEXT_COLOR};">${escapeHtml(dto.title)}</span>
  </td>
</tr>`;

  const candidateRows = dto.items
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((item) => buildCandidateCard(item.title, item.titleColor, item.chips))
    .join('\n');

  const shareUrl = `${BASE_URL}${dto.betSlipLink}${dto.betSlipLink.includes('?') ? '&' : '?'}show_share_dialog=1`;
  const oddsUrl = `${BASE_URL}${dto.betSlipLink}`;
  const boardUrl = `${BASE_URL}${ELECTION_BOARD_PATH}`;

  const buttons = [
    buildCtaButton('Forward', shareUrl, false),
    buildCtaButton('View Current Odds', oddsUrl, false),
    buildCtaButton('View Election Board', boardUrl, true),
  ].join('\n');

  const buttonsRow = `<tr>
  <td style="padding: 16px 28px;">
    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse;">
      <tr>
        ${buttons}
      </tr>
    </table>
  </td>
</tr>`;

  return `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse; max-width: 600px; background-color: #ffffff;" bgcolor="#ffffff" align="center">
${headerRow}
${candidateRows}
${buttonsRow}
</table>`;
}

export async function copyHtmlToClipboard(html: string): Promise<boolean> {
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      const blob = new Blob([html], { type: 'text/html' });
      const item = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([item]);
      return true;
    }

    // Fallback: hidden div + execCommand
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.opacity = '0';
    document.body.appendChild(container);

    const range = document.createRange();
    range.selectNodeContents(container);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    const success = document.execCommand('copy');
    document.body.removeChild(container);
    return success;
  } catch (err) {
    console.error(err);
    return false;
  }
}
